import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "./api/axios";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const codeParam = searchParams.get("code");
    if (codeParam) {
      setCode(codeParam);
    } else {
      setMessage("Kode verifikasi tidak ditemukan di URL");
      setTimeout(() => {
        navigate("/forgot-password");
      }, 3000);
    }
  }, [searchParams, navigate]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return "Password minimal 6 karakter";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setIsSuccess(false);

    // Validasi form
    if (!code) {
      setMessage("Kode verifikasi tidak valid");
      setIsLoading(false);
      return;
    }

    if (!newPassword.trim()) {
      setMessage("Password baru tidak boleh kosong");
      setIsLoading(false);
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setMessage(passwordError);
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage("Konfirmasi password tidak cocok");
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post("/update-password", {
        code: code,
        new_password: newPassword,
      });

      if (response.status === 200 && response.data?.message === "password changed successfully") {
        setIsSuccess(true);
        setMessage("Password berhasil diubah! Anda akan diarahkan ke halaman login.");
        
        // Redirect ke login setelah 3 detik
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setMessage("Terjadi kesalahan. Silakan coba lagi.");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      if (error.response?.data?.error) {
        // Handle specific error messages from backend
        const errorMsg = error.response.data.error;
        if (errorMsg.includes("invalid") || errorMsg.includes("expired")) {
          setMessage("Kode verifikasi tidak valid atau sudah kedaluwarsa");
        } else {
          setMessage(errorMsg);
        }
      } else {
        setMessage("Terjadi kesalahan koneksi. Silakan coba lagi.");
      }
    } finally {
      setIsLoading(false);
    }
  };




  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header with back button */}
      <div className="flex items-center justify-between px-4 py-4 pt-8">
        <button
          onClick={() => navigate("/forgot-password")}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          disabled={isLoading}
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <div className="flex-1" />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-32">
        <div className="flex flex-col items-center px-8 py-4">
          <div className="mb-6 text-left w-full max-w-md">
            <img src="/logohallostroke.png" alt="Logo" className="h-12 mb-8" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Reset Password
            </h1>
            <p className="text-gray-600 text-sm mb-8">
              Masukkan password baru untuk akun Anda. Pastikan password aman dan mudah diingat.
            </p>
          </div>

          <div className="w-full max-w-md">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Kode Verifikasi (Read-only) */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kode Verifikasi
                </label>
                <input
                  type="text"
                  value={code}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Kode ini diperoleh dari link yang dikirim ke WhatsApp Anda
                </p>
              </div> */}

              {/* Password Baru */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Baru<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Masukkan password baru"
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimal 6 karakter. Gunakan kombinasi huruf, angka, dan simbol untuk keamanan lebih baik.
                </p>
              </div>

              {/* Konfirmasi Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Konfirmasi Password<span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Konfirmasi password baru"
                    className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">
                    Konfirmasi password tidak cocok
                  </p>
                )}
                {confirmPassword && newPassword === confirmPassword && confirmPassword.length >= 6 && (
                  <p className="text-xs text-green-500 mt-1">
                    Password cocok ✓
                  </p>
                )}
              </div>

              {/* Message Display */}
              {message && (
                <div className="mt-4 mb-4">
                  <div className={`border rounded-lg p-4 ${
                    isSuccess
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg
                          className={`h-5 w-5 ${
                            isSuccess ? "text-green-400" : "text-red-400"
                          }`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          {isSuccess ? (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          ) : (
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"
                            />
                          )}
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${
                          isSuccess ? "text-green-800" : "text-red-800"
                        }`}>
                          {message}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-right mt-4">
                <span className="text-red-500">*</span>
                <span className="font-semibold text-black">wajib diisi</span>
              </p>
            </form>
          </div>

          {/* Add extra space at bottom for better scrolling */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-8 pb-6 pt-4 shadow-lg border-t border-gray-100">
        <div className="w-full max-w-md mx-auto">
          <button
            onClick={handleSubmit}
            type="button"
            className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 transition mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword || isSuccess}
          >
            {isLoading ? "Mengubah Password..." : isSuccess ? "Password Berhasil Diubah" : "Ubah Password"}
          </button>

          <div className="flex items-center justify-between my-2">
            <hr className="w-full border-gray-300" />
            <span className="mx-2 text-gray-500 text-sm">atau</span>
            <hr className="w-full border-gray-300" />
          </div>

          <button
            onClick={() => navigate("/login")}
            className="w-full border border-blue-500 text-blue-500 py-2 rounded-md text-base font-medium hover:bg-blue-50 transition mt-3 disabled:opacity-50"
            disabled={isLoading}
          >
            Kembali ke Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;